import { createContext, type ReactNode, useContext, useReducer, useEffect } from 'react';
import { API_CONFIG, API_ENDPOINTS } from '~/config/constants';
import { UserEntity, AuthState, AuthActions } from '~/core/domain/entities/user_entity';
import { DIContainer } from '~/di/container';
import { AuthController, useAuthController } from '~/core/presentation/controllers/auth_controller';

const AuthReducer = (state: AuthState, action: AuthActions): AuthState => {
  console.log('🔄 AuthReducer - Ação recebida:', action.type, action);
  
  switch (action.type) {
    case 'SIGN_IN':
      console.log('✅ AuthReducer - Fazendo login:', action.payload.user);
      return {
        state: 'SIGNED_IN',
        currentUser: action.payload.user,
      };
    case 'SIGN_OUT':
      console.log('🚪 AuthReducer - Fazendo logout');
      return {
        state: 'SIGNED_OUT',
      };
    default:
      return state;
  }
};

type AuthContextProps = {
  state: AuthState;
  dispatch: (value: AuthActions) => void;
  authController: AuthController;
};

const diContainer = DIContainer.getInstance();
const authController = new AuthController(
  diContainer.getLoginUseCase(),
  diContainer.getLogoutUseCase(),
  diContainer.getGetPersistedTokenUseCase()
);

export const AuthContext = createContext<AuthContextProps>({
  state: { state: 'UNKNOWN' },
  dispatch: () => {},
  authController,
});

const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(AuthReducer, { state: 'UNKNOWN' });

  // Verifica token persistido ao inicializar
  useEffect(() => {
    const checkPersistedToken = async () => {
      console.log('🔍 Verificando token persistido...');

      try {
        const result = await authController.getPersistedToken();
        console.log('📋 Resultado da verificação de token:', result);

        if (!result.token) {
          console.log('❌ Nenhum token encontrado');
          dispatch({ type: 'SIGN_OUT' });
          return;
        }

        console.log('✅ Token encontrado, tentando hidratar usuário');
        const diContainer = DIContainer.getInstance();
        const authRepository = diContainer.getAuthRepository();
        const tokenEntity = await authRepository.getPersistedToken();

        console.log('🔑 Token entity recuperado (cookies):', tokenEntity);

        // Se já temos user persistido, usa direto
        if (tokenEntity?.user) {
          console.log('👤 Usuário já persistido nos cookies');
          dispatch({ type: 'SIGN_IN', payload: { user: tokenEntity.user } });
          return;
        }

        // Caso contrário buscar no backend /auth/me
        try {
          console.log('🌐 Buscando /auth/me para hidratar usuário...');
          const meResp = await fetch(`${API_CONFIG.baseURL}${API_ENDPOINTS.AUTH.ME}`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${result.token}`,
              'Content-Type': 'application/json'
            },
            credentials: 'include'
          });

          if (!meResp.ok) {
            console.warn('⚠️ Falha ao obter /auth/me:', meResp.status, meResp.statusText);
            dispatch({ type: 'SIGN_OUT' });
            return;
          }

          const meData = await meResp.json();
          const user = meData.user || meData.data || meData; // fallback flexível
          console.log('👤 /auth/me retornou usuário:', { id: user?.id, role: user?.role });

            if (user && user.id) {
            // Persistir novamente incluindo user
            await authRepository.persistToken({
              token: result.token,
              expiresAt: tokenEntity?.expiresAt,
              user
            });
            dispatch({ type: 'SIGN_IN', payload: { user } });
          } else {
            console.warn('⚠️ /auth/me não retornou usuário válido');
            dispatch({ type: 'SIGN_OUT' });
          }
        } catch (err) {
          console.error('💥 Erro ao buscar /auth/me:', err);
          dispatch({ type: 'SIGN_OUT' });
        }
      } catch (error) {
        console.error('💥 Erro ao verificar token persistido:', error);
        dispatch({ type: 'SIGN_OUT' });
      }
    };

    checkPersistedToken();
  }, []);

  return (
    <AuthContext.Provider value={{ state, dispatch, authController }}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuthState = () => {
  const { state } = useContext(AuthContext);
  return {
    state,
  };
};

const useSignIn = () => {
  const { dispatch, authController } = useContext(AuthContext);
  const { login, isLoading, error } = useAuthController(authController);
  
  return {
    signIn: async (email: string, password: string) => {
      const result = await login({ email, password });
      
      if (result.success) {
        // Em uma implementação real, você buscaria os dados do usuário após o login
        const mockUser: UserEntity = {
          id: '1',
          email,
          name: 'User Name',
        };
        dispatch({ type: 'SIGN_IN', payload: { user: mockUser } });
      }
      
      return result;
    },
    isLoading,
    error,
  };
};

const useSignOut = () => {
  const { dispatch, authController } = useContext(AuthContext);
  const { logout, isLoading, error } = useAuthController(authController);
  
  return {
    signOut: async () => {
      console.log('🚪 useSignOut - Iniciando processo de logout...');
      
      const result = await logout();
      console.log('📊 useSignOut - Resultado do logout:', result);
      
      if (result.success) {
        console.log('✅ useSignOut - Logout bem-sucedido, atualizando contexto');
        dispatch({ type: 'SIGN_OUT' });
      } else {
        console.error('❌ useSignOut - Logout falhou:', result.error);
        // Mesmo com erro no logout remoto, atualiza o contexto local
        dispatch({ type: 'SIGN_OUT' });
      }
      
      return result;
    },
    isLoading,
    error,
  };
};

export { useAuthState, useSignIn, useSignOut, AuthProvider };
